import './tcrs2-tailwind-reset.css';
import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import LoadingSpinner from '../ReusableComponents/LoadingSpinner';
import SuccessCheckmark from './SuccessCheckmark';
import FloatingLabelInput from '../ReusableComponents/FloatingLabelInput';
import SettingsService from '../../Core/Services/SettingsService';
import AuthenticationService from '../../Core/Services/AuthenticationService';
import EyeIcon from '../Icons/EyeIcon';

const COUNTRIES = [SettingsService.getCountry()];
const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[\d\s+()-]{8,}$/;

const errorClass = 'text-red-600 text-xs mt-1';

export default function CreateAccountForm({
  handleNext,
  setUser,
  setShowLogin,
  setCvr,
}) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formValues, setFormValues] = useState({ country: COUNTRIES[0] });
  const [isCompany, setIsCompany] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gdprChecked, setGdprChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showTermsTooltip, setShowTermsTooltip] = useState(false);

  const handleChange = (key, value) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  const validateField = (key, value) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    switch (key) {
      case 'email':
        if (!trimmedValue) return t('email_required') || 'Email is required';
        if (!EMAIL_PATTERN.test(trimmedValue))
          return (
            t('invalid_email_format') || 'Please enter a valid email address'
          );
        break;
      case 'phone':
        if (!trimmedValue)
          return t('phone_required') || 'Phone number is required';
        if (!PHONE_PATTERN.test(trimmedValue))
          return (
            t('invalid_phone_format') ||
            'Please enter a valid phone number (minimum 8 digits)'
          );
        break;
      case 'firstName':
      case 'lastName':
        if (!trimmedValue)
          return (
            t(`${key}_required`) ||
            `${key.charAt(0).toUpperCase() + key.slice(1)} is required`
          );
        if (trimmedValue.length < 2)
          return t('name_too_short') || 'Name must be at least 2 characters';
        break;
      case 'password':
        if (!trimmedValue)
          return t('password_required') || 'Password is required';
        if (!PASSWORD_PATTERN.test(trimmedValue))
          return (
            t('password_requirements') ||
            'Password must contain at least 6 characters, one uppercase, one lowercase, one number, and one special character'
          );
        break;
      case 'confirmPassword':
        if (trimmedValue !== formValues.password)
          return t('passwords_do_not_match') || 'Passwords do not match';
        break;
      case 'postalCode':
        if (!trimmedValue)
          return t('postal_code_required') || 'Postal code is required';
        break;
      default:
        return null;
    }
    return null;
  };

  const handleFormSubmit = async e => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const errors = {};
    const requiredFields = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'password',
      'confirmPassword',
      'address',
      'postalCode',
      'city',
      'country',
    ];
    for (const field of requiredFields) {
      const err = validateField(field, formValues[field]);
      if (err) errors[field] = err;
    }
    for (const field of ['firstName', 'lastName', 'address', 'city']) {
      if (formValues[field] && !formValues[field].trim()) {
        errors[field] =
          t('field_cannot_be_empty') || 'This field cannot be empty';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(
        t('please_fix_errors_before_submitting') ||
          'Please fix the errors in the form before submitting'
      );
      return;
    }
    if (!gdprChecked || !termsChecked) {
      setError(
        t('accept_terms_and_conditions') ||
          'You must accept the terms and conditions and privacy policy'
      );
      return;
    }

    setSubmitting(true);
    try {
      const registerResponse = await AuthenticationService.register(formValues);
      if (!registerResponse || !registerResponse.success) {
        let errorMessages =
          t('registration_failed') || 'Registration failed. Please try again.';
        if (registerResponse?.errors) {
          if (typeof registerResponse.errors === 'string') {
            errorMessages = registerResponse.errors;
          } else if (typeof registerResponse.errors === 'object') {
            errorMessages = Object.entries(registerResponse.errors)
              .map(([key, arr]) =>
                Array.isArray(arr)
                  ? arr.map(msg => `${key}: ${msg}`)
                  : `${key}: ${arr}`
              )
              .flat()
              .join('\n');
          }
        }
        throw new Error(errorMessages);
      }
      const loginResponse = await AuthenticationService.login(
        formValues.email,
        formValues.password
      );
      if (loginResponse?.accessToken) {
        AuthenticationService.saveToken(loginResponse.accessToken);
        const userInfo = await AuthenticationService.validateToken();
        if (!userInfo)
          throw new Error(
            t('failed_to_get_user_info') ||
              'Failed to retrieve user information'
          );
        setUser(userInfo);
        setSuccess(true);
        setError('');
        setSubmitting(false);
        handleNext();
      } else {
        throw new Error(
          t('login_failed_after_registration') ||
            'Account created successfully, but automatic login failed. Please log in manually.'
        );
      }
      setSubmitting(false);
      setError('');
    } catch (err) {
      setSubmitting(false);
      setError(err.message);
    }
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <div
        className="mb-6 border-gray-300 !border !border-solid p-6 flex items-center gap-2"
        style={{ backgroundColor: '#F5F5F5' }}
      >
        <div className="text-base">{t('already_have_account')}</div>
        <span
          className="underline text-blue-600 cursor-pointer text-base"
          onClick={() => setShowLogin(true)}
        >
          {t('log_in_here')}
        </span>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4 text-center">
          {error}
        </div>
      )}

      {/* Personal info */}
      <div className="mb-6 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <FloatingLabelInput
              label={t('first_name')}
              value={formValues.firstName || ''}
              onChange={e => handleChange('firstName', e.target.value)}
              error={!!fieldErrors.firstName}
              required
            />
            {fieldErrors.firstName && (
              <p className={errorClass}>{fieldErrors.firstName}</p>
            )}
          </div>

          <div>
            <FloatingLabelInput
              label={t('last_name')}
              value={formValues.lastName || ''}
              onChange={e => handleChange('lastName', e.target.value)}
              error={!!fieldErrors.lastName}
              required
            />
            {fieldErrors.lastName && (
              <p className={errorClass}>{fieldErrors.lastName}</p>
            )}
          </div>

          <div className="col-span-1 md:col-span-2">
            <FloatingLabelInput
              label="Email"
              type="email"
              value={formValues.email || ''}
              onChange={e => handleChange('email', e.target.value)}
              error={!!fieldErrors.email}
              required
            />
            {fieldErrors.email && (
              <p className={errorClass}>{fieldErrors.email}</p>
            )}
          </div>

          <div className="col-span-1 md:col-span-2">
            <FloatingLabelInput
              label={t('phone_number')}
              type="tel"
              value={formValues.phone || ''}
              onChange={e => handleChange('phone', e.target.value)}
              error={!!fieldErrors.phone}
              required
            />
            {fieldErrors.phone && (
              <p className={errorClass}>{fieldErrors.phone}</p>
            )}
          </div>

          <div className="col-span-1 md:col-span-2">
            <label className="flex items-center gap-2 mb-2 text-sm">
              <input
                type="checkbox"
                checked={isCompany}
                onChange={() => setIsCompany(v => !v)}
                className="h-4 w-4"
              />
              {t('registering_as_company')}
            </label>
            {isCompany && (
              <FloatingLabelInput
                label={t('cvr_only_for_companies')}
                value={formValues.cvr || ''}
                onChange={e => {
                  setCvr(e.target.value);
                  handleChange('cvr', e.target.value);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Address info */}
      <div className="mb-6 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="col-span-1 md:col-span-12">
            <FloatingLabelInput
              label={t('country')}
              value={formValues.country?.toUpperCase() || COUNTRIES[0]}
              onChange={() => {}}
              disabled
              required
            />
            {fieldErrors.country && (
              <p className={errorClass}>{fieldErrors.country}</p>
            )}
          </div>

          <div className="col-span-1 md:col-span-12">
            <FloatingLabelInput
              label={t('address')}
              value={formValues.address || ''}
              onChange={e => handleChange('address', e.target.value)}
              error={!!fieldErrors.address}
              required
            />
            {fieldErrors.address && (
              <p className={errorClass}>{fieldErrors.address}</p>
            )}
          </div>

          <div className="col-span-1 md:col-span-4">
            <FloatingLabelInput
              label={t('postal_code')}
              value={formValues.postalCode || ''}
              onChange={e => handleChange('postalCode', e.target.value)}
              error={!!fieldErrors.postalCode}
              required
            />
            {fieldErrors.postalCode && (
              <p className={errorClass}>{fieldErrors.postalCode}</p>
            )}
          </div>

          <div className="col-span-1 md:col-span-8">
            <FloatingLabelInput
              label={t('city')}
              value={formValues.city || ''}
              onChange={e => handleChange('city', e.target.value)}
              error={!!fieldErrors.city}
              required
            />
            {fieldErrors.city && (
              <p className={errorClass}>{fieldErrors.city}</p>
            )}
          </div>
        </div>
      </div>

      {/* Password */}
      <div className="mb-6 pb-4 !border-solid !border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <FloatingLabelInput
              label={t('password')}
              type={showPassword ? 'text' : 'password'}
              value={formValues.password || ''}
              onChange={e => handleChange('password', e.target.value)}
              error={!!fieldErrors.password}
              required
              suffix={
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(v => !v)}
                  className="focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon open={showPassword} />
                </button>
              }
            />
            {fieldErrors.password ? (
              <p className={errorClass}>{fieldErrors.password}</p>
            ) : (
              <p className="text-gray-500 text-xs mt-1">
                {t('password_hint') ||
                  'Use at least 6 characters, one uppercase, one lowercase, one number and one special character.'}
              </p>
            )}
          </div>

          <div>
            <FloatingLabelInput
              label={t('confirm_password')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={formValues.confirmPassword || ''}
              onChange={e => handleChange('confirmPassword', e.target.value)}
              error={!!fieldErrors.confirmPassword}
              required
              suffix={
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirmPassword(v => !v)}
                  className="focus:outline-none"
                  aria-label={
                    showConfirmPassword ? 'Hide password' : 'Show password'
                  }
                >
                  <EyeIcon open={showConfirmPassword} />
                </button>
              }
            />
            {fieldErrors.confirmPassword && (
              <p className={errorClass}>{fieldErrors.confirmPassword}</p>
            )}
          </div>
        </div>
      </div>

      {/* Terms */}
      <div className="pt-4">
        <div className="mb-4">
          <label className="flex items-start gap-2 text-gray-700 text-sm">
            <input
              type="checkbox"
              checked={gdprChecked && termsChecked}
              onChange={() => {
                setGdprChecked(v => !v);
                setTermsChecked(v => !v);
              }}
              className="h-4 w-4 mt-1 shrink-0"
            />
            <span className="flex flex-wrap items-center gap-1">
              <Trans
                i18nKey="i_accept_terms_and_conditions"
                components={{
                  termsLink: (
                    <a
                      href={SettingsService.getTermsAndConditionsUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-primaryblue"
                      style={{ color: '#444' }}
                    />
                  ),
                  privacyLink: (
                    <a
                      href={SettingsService.getPrivacyPolicyUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-primaryblue"
                      style={{ color: '#444' }}
                    />
                  ),
                  onlineTermsLink: (
                    <span
                      style={{ color: '#444' }}
                      className="underline cursor-pointer"
                      onClick={() => setShowTermsTooltip(!showTermsTooltip)}
                    />
                  ),
                }}
              />
              <div className="relative inline-block">
                {showTermsTooltip && (
                  <div
                    className="absolute bottom-full left-0 mb-2 w-[90vw] max-w-[400px] max-h-[60vh] overflow-y-auto p-4 bg-white border border-gray-300 rounded shadow-lg z-50 text-xs text-left"
                    style={{ color: '#444' }}
                  >
                    <p className="font-bold mb-2">
                      {t('online_payment_terms_heading')}
                    </p>
                    <p className="mb-2">{t('online_payment_terms_intro')}</p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_booking_heading')}
                    </p>
                    <p className="mb-2">{t('online_payment_terms_booking')}</p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_subscription_heading')}
                    </p>
                    <p className="mb-2">
                      {t('online_payment_terms_subscription')}
                    </p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_billing_heading')}
                    </p>
                    <p className="mb-2">{t('online_payment_terms_billing')}</p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_update_heading')}
                    </p>
                    <p className="mb-2">{t('online_payment_terms_update')}</p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_failure_heading')}
                    </p>
                    <p className="mb-2">{t('online_payment_terms_failure')}</p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_cancellation_heading')}
                    </p>
                    <p className="mb-0">
                      {t('online_payment_terms_cancellation')}
                    </p>
                  </div>
                )}
              </div>
            </span>
          </label>
        </div>

        {success ? (
          <SuccessCheckmark />
        ) : submitting ? (
          <div className="flex justify-center py-2">
            <LoadingSpinner />
          </div>
        ) : (
          <button
            type="submit"
            id="tcrs-button"
            className="w-full text-white shadow font-semibold text-base py-2 bg-primaryblue hover:bg-primaryblue/90 focus:bg-primaryblue/90 border-none"
          >
            {t('confirm_booking')}
          </button>
        )}
      </div>
    </form>
  );
}
